import React, { useState, useEffect, useRef, useCallback } from "react";
import { format as dateFormat, add as dateAdd } from "date-fns";
import { DiliTextCP } from "../Utility/DiliTextCP";
import {
  dateObjectFromDateInputString,
  dateZeroTime,
  getDifferenceInDaysForDateInputStrings,
} from "../../helpers/utilities";
import { Combobox } from "../Utility/Combobox";
import handleApiError from "../../helpers/handleApiError";
import { useHistory } from "react-router-dom";
import { getCDKSearchSuggestionsByInvoiceOrCustomer } from "../../services/cdkOrderService";
import { debounce } from "lodash";

const CDKOrderSearchFormCategories = {
  invoice: "Invoice",
  customer: "Customer",
};

const CDKOrderSearchDateRangeLimit = 6; //For a 1 week limit we are doing a day difference of 6 days as we are doing the search inclusively (i.e. 6-1 to 6-7 is a 6 day difference but is 7 days worth of orders if you are seaching inclusively), but we add a day before we send the request as the backend is not inclusive and can have a 7 day difference.
const purgeWeekLimit = 6;
export const CDKOrderImportSearchForm = ({
  searchTypes,
  handleSubmitCDKSearch,
  defaultFromOffset,
  defaultToOffset,
}) => {
  const history = useHistory();
  const [formDates, setFormDates] = useState({
    fromDate: dateFormat(
      dateAdd(new Date(), { days: defaultFromOffset }),
      "yyyy-MM-dd"
    ),
    toDate: dateFormat(
      dateAdd(new Date(), { days: defaultToOffset }),
      "yyyy-MM-dd"
    ),
  });
  const handleChangeFormDates = (e) => {
    setFormDates({ ...formDates, [e.target.name]: e.target.value });
  };

  const dateRangeOverLimit =
    getDifferenceInDaysForDateInputStrings(
      formDates.toDate,
      formDates.fromDate
    ) > CDKOrderSearchDateRangeLimit;
  const fromDateObject = dateObjectFromDateInputString(formDates.fromDate);
  const purgeLimitDate = dateAdd(dateZeroTime(new Date()), {
    weeks: -purgeWeekLimit,
  });
  const searchTooFarBack = fromDateObject < purgeLimitDate;

  const fromAndToMixed = formDates.toDate < formDates.fromDate;

  const datesInvalid = dateRangeOverLimit || searchTooFarBack || fromAndToMixed;
  // console.log(datesInvalid);
  // console.log(fromDateObject);
  // console.log(purgeLimitDate);
  // console.log(fromDateObject < purgeLimitDate);

  //Combobox Setup
  const [currentSearchValue, setCurrentSearchValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [currentSearchCategory, setCurrentSearchCategory] = useState("Invoice");
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const abortControllerRef = useRef(new AbortController());
  const searchTermMinLength = 3;

  const handleChangeCategory = (value) => {
    setCurrentSearchCategory(value);
    setSelectedOptions([]);
    setCurrentSearchValue("");
  };

  const fetchSuggestions = async (currentSearchCategory, searchTerm) => {
    if (searchTerm.length < searchTermMinLength) {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort(); //Abort the last request, if there was one.
      }
      return;
    }
    try {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort(); //Abort the last request, if there was one.
      }
      abortControllerRef.current = new AbortController(); //Make a new abort controller for this request.
      // console.log("about to send fetch");
      // console.log("sent fetch: ", barcodeSearchTerm);
      const possibleBarcodesResponse =
        await getCDKSearchSuggestionsByInvoiceOrCustomer(
          currentSearchCategory === "Invoice",
          searchTerm,
          abortControllerRef.current.signal
        );
      // console.log(possibleBarcodesResponse);
      // console.log("finish fetch");
      setSuggestions(possibleBarcodesResponse.data);
      // console.log("set possible barcodes");

      setLoadingSuggestions(false);
      // console.log("set loading barcodes to false");
    } catch (ex) {
      setLoadingSuggestions(false);
      handleApiError(ex, history);
    }
  };

  const debouncedFetchSuggestions = useCallback(
    debounce(fetchSuggestions, 400),
    []
  );

  useEffect(() => {
    const fetchSuggestionsEffect = async () => {
      if (currentSearchValue.length >= searchTermMinLength) {
        setLoadingSuggestions(true);
      } else if (currentSearchValue.length < searchTermMinLength) {
        setSuggestions([]);
        setLoadingSuggestions(false);
      }
      await debouncedFetchSuggestions(
        currentSearchCategory,
        currentSearchValue
      );
    };
    fetchSuggestionsEffect();
  }, [currentSearchValue, currentSearchCategory, debouncedFetchSuggestions]);

  return (
    <div className="tw-mb-3 lg:tw-mb-8 tw-mt-4">
      <div className="tw-flex tw-flex-col tw-gap-y-3 lg:tw-flex-none lg:tw-grid lg:tw-grid-cols-12 lg:tw-gap-x-10 lg:tw-justify-between">
        <div className="tw-self-end tw-w-full lg:tw-col-span-3 lg:tw-flex lg:tw-justify-between">
          <div className="lg:tw-flex-1 lg:tw-max-w-sm">
            <Combobox
              currentSearchTerm={currentSearchValue}
              setCurrentSearchTerm={setCurrentSearchValue}
              options={suggestions}
              selectedOptions={selectedOptions}
              setSelectedOptions={setSelectedOptions}
              multiselect={true}
              loading={loadingSuggestions}
              categories={Object.values(CDKOrderSearchFormCategories).map(
                (category) => {
                  return { display: category, value: category };
                }
              )}
              currentCategory={currentSearchCategory}
              noResultsMessage={
                loadingSuggestions
                  ? "Loading Suggestions"
                  : currentSearchValue.length >= searchTermMinLength
                  ? "No Suggestions Found"
                  : `Start typing to load suggestions (min. ${searchTermMinLength} characters)`
              }
              setCurrentCategory={handleChangeCategory}
              // dropdownProps={{
              //   styleMergeDropdownOption: {
              //     overflow: "hidden",
              //     textOverflow: "ellipsis",
              //   },
              // }}
            />
          </div>
          <div className="tw-hidden lg:tw-block lg:tw-text-center lg:tw-self-end lg:tw-max-w-lg lg:tw-font-semibold lg:tw-text-xl lg:tw-mb-2">
            or
          </div>
        </div>

        <DiliTextCP
          className="tw-text-left lg:tw-col-span-3 lg:tw-self-end"
          type="date"
          label="Imported From"
          name="fromDate"
          value={formDates.fromDate}
          onChange={handleChangeFormDates}
          max="2099-12-30"
          disabled={currentSearchValue.length > 0 || selectedOptions.length > 0}
        />

        <DiliTextCP
          className="tw-text-left lg:tw-col-span-3 lg:tw-self-end"
          type="date"
          label="Imported To"
          name="toDate"
          value={formDates.toDate}
          onChange={handleChangeFormDates}
          disabled={currentSearchValue.length > 0 || selectedOptions.length > 0}
        />

        <div className="tw-text-center lg:tw-text-left lg:tw-self-end lg:tw-col-span-3">
          <button
            className="btn btn-primary"
            disabled={datesInvalid}
            onClick={() => {
              const selectedOptionsAndSearchTerm = [
                ...selectedOptions,
                currentSearchValue,
              ];
              handleSubmitCDKSearch(
                selectedOptions.length > 0 || currentSearchValue.length > 0
                  ? currentSearchCategory
                  : searchTypes.dateRange,
                formDates.fromDate,
                formDates.toDate,
                selectedOptionsAndSearchTerm,
                selectedOptionsAndSearchTerm,
                false
              );
            }}
          >
            Submit
          </button>
        </div>

        <div className="lg:tw-col-span-4 lg:tw-col-start-4">
          {datesInvalid && (
            <p className="text-danger font-small">
              {fromAndToMixed
                ? "To date is before from date"
                : searchTooFarBack
                ? `Orders older than ${purgeWeekLimit} weeks are purged`
                : dateRangeOverLimit
                ? `Date range cannot be over ${
                    CDKOrderSearchDateRangeLimit + 1
                  } days`
                : ""}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
